import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/Card';

export function TermsOfService() {
  const { t } = useTranslation();

  return (
    <div className="container-app py-12">
      <div className="mx-auto max-w-3xl">
        <h1 className="mb-8 text-3xl font-bold sm:text-4xl">
          {t('terms.title', 'Terms of Service')}
        </h1>

        <Card variant="bordered">
          <CardContent className="prose dark:prose-invert max-w-none">
            <p className="text-sm text-gray-900 dark:text-gray-200">
              Last updated: {new Date().toLocaleDateString()}
            </p>

            <h2>{t('terms.acceptance', 'Acceptance of terms')}</h2>
            <p>
              By accessing and using Do You Like This Work? (the "Service"), you accept
              and agree to be bound by these Terms of Service. If you do not agree to
              these terms, please do not use the Service.
            </p>

            <h2>{t('terms.description', 'Description of service')}</h2>
            <p>
              Do You Like This Work? is a research project that collects data on aesthetic
              preferences and perceptions of AI-generated versus human-created artwork.
              The Service allows you to:
            </p>
            <ul>
              <li>Vote on whether you like displayed images</li>
              <li>Provide reasons for your aesthetic preferences</li>
              <li>View aggregate statistics</li>
              <li>Suggest images for inclusion in the collection</li>
            </ul>

            <h2>{t('terms.user_conduct', 'User conduct')}</h2>
            <p>
              When using the Service, you agree to:
            </p>
            <ul>
              <li>Provide honest responses to voting prompts</li>
              <li>Not attempt to manipulate voting results through automation or duplicate votes</li>
              <li>Not submit spam, malicious links, or inappropriate content through the suggestion form</li>
              <li>Not reverse-engineer, scrape, or attempt to extract data beyond what is publicly displayed</li>
              <li>Respect the intellectual property rights of image creators</li>
            </ul>

            <h2>{t('terms.content', 'Content and intellectual property')}</h2>
            <p>
              Images displayed in the Service are properly attributed to their creators
              and sources. We make reasonable efforts to:
            </p>
            <ul>
              <li>Verify the origin of each image (human-made vs. AI-generated)</li>
              <li>Provide proper attribution and source links</li>
              <li>Respect copyright and licensing terms</li>
              <li>Remove content upon valid takedown requests</li>
            </ul>
            <p>
              If you believe your work has been used without proper authorization,
              please contact us with details and we will investigate promptly.
            </p>

            <h2>{t('terms.data_use', 'Data collection and use')}</h2>
            <p>
              By using the Service, you consent to the collection and use of anonymized
              data as described in our Privacy Policy. This includes:
            </p>
            <ul>
              <li>Your votes and reasoning</li>
              <li>Aggregate demographic information (country/region)</li>
              <li>Timing data for research purposes</li>
            </ul>

            <h2>{t('terms.disclaimer', 'Disclaimer of warranties')}</h2>
            <p>
              The Service is provided "as is" without warranties of any kind. We do not
              guarantee:
            </p>
            <ul>
              <li>Uninterrupted or error-free operation</li>
              <li>Absolute accuracy of image attribution or AI detection</li>
              <li>Permanent availability of the Service or any specific feature</li>
            </ul>

            <h2>{t('terms.limitation', 'Limitation of liability')}</h2>
            <p>
              To the fullest extent permitted by law, we shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages arising
              from your use of the Service.
            </p>

            <h2>{t('terms.research', 'Research purposes')}</h2>
            <p>
              This Service is operated for research purposes. Aggregate data and findings
              may be:
            </p>
            <ul>
              <li>Published in academic papers or blog posts</li>
              <li>Shared with the research community</li>
              <li>Used to inform discussions about AI and creativity</li>
            </ul>
            <p>
              Individual responses will never be published or attributed to specific users.
            </p>

            <h2>{t('terms.api_access', 'API and data access')}</h2>
            <p>
              Aggregate statistics are available through our public API. Researchers
              may request access to anonymized datasets for academic purposes. Automated
              scraping or bulk data extraction is prohibited without prior authorization.
            </p>

            <h2>{t('terms.modifications', 'Modifications to service')}</h2>
            <p>
              We reserve the right to:
            </p>
            <ul>
              <li>Modify or discontinue the Service at any time</li>
              <li>Update these Terms of Service with notice</li>
              <li>Add, remove, or modify images in the collection</li>
              <li>Change voting mechanisms or data collection methods</li>
            </ul>

            <h2>{t('terms.termination', 'Termination')}</h2>
            <p>
              We may restrict or terminate access to the Service for users who violate
              these terms, including those who attempt to manipulate voting results or
              submit spam.
            </p>

            <h2>{t('terms.governing_law', 'Governing law')}</h2>
            <p>
              These Terms shall be governed by and construed in accordance with applicable
              laws. Any disputes shall be resolved through good faith negotiation or, if
              necessary, in the appropriate jurisdiction.
            </p>

            <h2>{t('terms.contact', 'Contact information')}</h2>
            <p>
              For questions about these Terms of Service, please open an issue on our
              GitHub repository or contact us through the project page.
            </p>

            <h2>{t('terms.entire_agreement', 'Entire agreement')}</h2>
            <p>
              These Terms of Service, together with our Privacy Policy, constitute the
              entire agreement between you and Do You Like This Work? regarding your
              use of the Service.
            </p>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-gray-900 hover:text-accent dark:text-gray-200"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t('common.back', 'Back')}
          </Link>
        </div>
      </div>
    </div>
  );
}
